/**
 * @fileoverview Web-side static constants for the legacy migration framework.
 * @module system-web/types/legacyMigrationConstants
 *
 * Responsibilities:
 * - Provide web-layer copies of KNOWN_COLLECTIONS and REGISTERED_ADAPTERS static manifests.
 * - These mirror the server-side constants in `@firesquirrel/legacy-migration` for use
 *   in React components without a build-time dependency on the API package.
 *
 * Dependencies:
 * - legacyMigrationTypes (local web-layer type definitions).
 *
 * Architecture:
 * - Must stay in sync with:
 *   - `api/packages/legacy-migration/src/core/knownCollectionsRegistry.ts` (KNOWN_COLLECTIONS)
 *   - `api/packages/legacy-migration/src/index.ts` (REGISTERED_ADAPTERS)
 *
 * Notes:
 * - KNOWN_COLLECTIONS is used by the dataset builder wizard for target collection selection.
 * - REGISTERED_ADAPTERS_MANIFEST is a lightweight summary of each adapter for the wizard Step 1
 *   "system-known" adapter selector — it does NOT include the full DatasetAdapter runtime object.
 * - Do NOT import `@firesquirrel/legacy-migration` in web packages — it is an API package.
 */

import type { KnownCollectionEntry } from './legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Known collections registry — mirrors api/packages/legacy-migration/src/core/knownCollectionsRegistry.ts
// ---------------------------------------------------------------------------

/**
 * @contract KNOWN_COLLECTIONS
 *
 * Purpose: All FireSight MongoDB collections that migration adapters may write into.
 * Fields: See KnownCollectionEntry.
 * Rules:
 * - requiredFields must be mapped for a template to activate.
 * - Must stay in sync with server-side registry.
 * Sync: Static — no runtime fetch required.
 */
export const KNOWN_COLLECTIONS: ReadonlyArray<KnownCollectionEntry> = [
  {
    name: 'violationCodeSets',
    requiredFields: ['name', 'type'],
    nullableFields: ['description', 'sortOrder', 'isActive'],
  },
  {
    name: 'violationCodes',
    requiredFields: ['codeSetId', 'code', 'description'],
    nullableFields: ['severity', 'section', 'isActive', 'sortOrder'],
  },
  {
    name: 'templates',
    requiredFields: ['name', 'templateType', 'status'],
    nullableFields: ['description', 'version', 'tags'],
  },
  {
    name: 'forms',
    requiredFields: ['templateId', 'name'],
    nullableFields: ['description', 'order', 'isRequired'],
  },
] as const;

// ---------------------------------------------------------------------------
// Registered adapters manifest — lightweight summary for UI (Step 1 system-known picker)
// ---------------------------------------------------------------------------

/**
 * @contract RegisteredAdapterManifest
 *
 * Purpose: Lightweight UI-only summary of a registered dataset adapter.
 * Fields:
 * - key: Adapter dataset key (matches server REGISTERED_ADAPTERS).
 * - displayName: Human-readable label.
 * - targetCollections: Collection names this adapter writes.
 * Rules: key must match a server adapter key exactly.
 * Sync: Static — must stay in sync with api/packages/legacy-migration/src/index.ts.
 */
export interface RegisteredAdapterManifest {
  key: string;
  displayName: string;
  targetCollections: string[];
}

/**
 * @contract REGISTERED_ADAPTERS_MANIFEST
 *
 * Purpose: UI-side summary of all registered dataset adapters for Step 1 system-known selection.
 * Rules: Must stay in sync with server REGISTERED_ADAPTERS.
 * Sync: Static — no runtime fetch required.
 */
export const REGISTERED_ADAPTERS_MANIFEST: ReadonlyArray<RegisteredAdapterManifest> = [
  {
    key: 'violationCodeSets',
    displayName: 'Violation Code Sets',
    targetCollections: ['violationCodeSets'],
  },
  {
    key: 'inspectionTemplates',
    displayName: 'Inspection Templates',
    targetCollections: ['templates', 'forms'],
  },
] as const;
